import type { ReactNode } from "react";

import { Code } from "@hightouch/htui";

import { StaticType, type VariableScope } from "src/formkit/components/types";

enum VariableType {
  CURRENT_TIMESTAMP = "current-timestamp",
  MODEL_ID = "model-id",
  MODEL_NAME = "model-name",
  SYNC_ID = "sync-id",
  SYNC_RUN_ID = "sync-run-id",
  OPERATION = "operation",
  MESSAGE_ID = "message-id",
  TRIGGER_EVENT_MESSAGE_ID = "trigger-event-message-id",
  TRIGGER_EVENT_TIMESTAMP = "trigger-event-timestamp",
  TRIGGER_EVENT_ORIGINAL_TIMESTAMP = "trigger-event-original-timestamp",
  TRIGGER_EVENT_SENT_AT = "trigger-event-sent-at",
  TRIGGER_EVENT_RECEIVED_AT = "trigger-event-received-at",
  USER_ID = "user-id",
  JOURNEY_ID = "journey-id",
  JOURNEY_NAME = "journey-name",
  NODE_ID = "node-id",
  NODE_NAME = "node-name",
}

type VariableSection =
  | "User"
  | "Model"
  | "Journey"
  | "Tile"
  | "Sync"
  | "Event"
  | "Time";

const VARIABLE_SECTION_ORDER: VariableSection[] = [
  "User",
  "Model",
  "Journey",
  "Tile",
  "Sync",
  "Event",
  "Time",
];

export type VariableOption = {
  description: ReactNode;
  label: string;
  section: VariableSection;
  type: StaticType;
  variable: VariableType;
};

/** A sync value, with the Liquid expression the template's metadata tab inserts for it. */
export type SyncVariableOption = VariableOption & { liquid: string };

export type VariableOptionSection<T extends VariableOption = VariableOption> = {
  label: VariableSection;
  options: T[];
};

const currentTimestampOption: VariableOption = {
  description: (
    <>
      The current timestamp as an ISO 8601 string (
      <Code>"YYYY-MM-DDTHH:mm:ss.sssZ"</Code>).
    </>
  ),
  label: "Current timestamp",
  section: "Time",
  type: StaticType.DATETIME,
  variable: VariableType.CURRENT_TIMESTAMP,
};

// A journey trigger has no sync run or model of its own. It carries the user and the journey step that fired.
export const journeyVariableOptions: VariableOption[] = [
  {
    description: <>The ID of the user this tile fired for, as a string.</>,
    label: "User ID",
    section: "User",
    type: StaticType.STRING,
    variable: VariableType.USER_ID,
  },
  {
    description: <>The ID of the current journey as a string.</>,
    label: "Journey ID",
    section: "Journey",
    type: StaticType.STRING,
    variable: VariableType.JOURNEY_ID,
  },
  {
    description: <>The name of the current journey as a string.</>,
    label: "Journey name",
    section: "Journey",
    type: StaticType.STRING,
    variable: VariableType.JOURNEY_NAME,
  },
  {
    description: <>The ID of this tile as a string.</>,
    label: "Tile ID",
    section: "Tile",
    type: StaticType.STRING,
    variable: VariableType.NODE_ID,
  },
  {
    description: <>The name of this tile as a string.</>,
    label: "Tile name",
    section: "Tile",
    type: StaticType.STRING,
    variable: VariableType.NODE_NAME,
  },
  {
    description: <>A unique ID for this send as a string.</>,
    label: "Message ID",
    section: "Tile",
    type: StaticType.STRING,
    variable: VariableType.MESSAGE_ID,
  },
  currentTimestampOption,
];

// The formkit context can name the scope outright. Otherwise the model decides between a batch and a realtime
// audience sync.
export function resolveVariableScope({
  variableScope,
  model,
}: {
  variableScope?: VariableScope;
  model?: { is_realtime_audience?: boolean | null } | undefined;
}): VariableScope {
  return (
    variableScope ??
    (model?.is_realtime_audience ? "realtime-audience" : "sync")
  );
}

// Realtime event fields are on `row` at mapping time, but they read as metadata, so templates insert them from here.
const realtimeEventVariableOptions: SyncVariableOption[] = [
  {
    description: (
      <>The messageId of the audience membership event being sent.</>
    ),
    label: "Message ID",
    liquid: "row['messageId']",
    section: "Event",
    type: StaticType.STRING,
    variable: VariableType.MESSAGE_ID,
  },
  {
    description: (
      <>
        Message ID of the specific event that triggered the audience membership
        change.
      </>
    ),
    label: "Trigger Event Message ID",
    liquid: "row['triggerEvent.messageId']",
    section: "Event",
    type: StaticType.STRING,
    variable: VariableType.TRIGGER_EVENT_MESSAGE_ID,
  },
  {
    description: (
      <>
        Timestamp of the specific event that triggered the audience membership
        change.
      </>
    ),
    label: "Trigger Event Timestamp",
    liquid: "row['triggerEvent.timestamp']",
    section: "Event",
    type: StaticType.DATETIME,
    variable: VariableType.TRIGGER_EVENT_TIMESTAMP,
  },
  {
    description: (
      <>
        OriginalTimestamp of the specific event that triggered the audience
        membership change.
      </>
    ),
    label: "Trigger Event OriginalTimestamp",
    liquid: "row['triggerEvent.originalTimestamp']",
    section: "Event",
    type: StaticType.DATETIME,
    variable: VariableType.TRIGGER_EVENT_ORIGINAL_TIMESTAMP,
  },
  {
    description: (
      <>
        SentAt timestamp of the specific event that triggered the audience
        membership change.
      </>
    ),
    label: "Trigger Event SentAt",
    liquid: "row['triggerEvent.sentAt']",
    section: "Event",
    type: StaticType.DATETIME,
    variable: VariableType.TRIGGER_EVENT_SENT_AT,
  },
  {
    description: (
      <>
        ReceivedAt timestamp of the specific event that triggered the audience
        membership change.
      </>
    ),
    label: "Trigger Event ReceivedAt",
    liquid: "row['triggerEvent.receivedAt']",
    section: "Event",
    type: StaticType.DATETIME,
    variable: VariableType.TRIGGER_EVENT_RECEIVED_AT,
  },
];

const batchSyncRunVariableOptions: SyncVariableOption[] = [
  {
    description: <>The ID of the current sync run as an integer.</>,
    label: "Sync run ID",
    liquid: "context['sync_run_id']",
    section: "Sync",
    type: StaticType.NUMBER,
    variable: VariableType.SYNC_RUN_ID,
  },
  {
    description: (
      <>
        The CDC operation <Code>add</Code>, <Code>change</Code>,
        <Code>remove</Code>, or <Code>all</Code> as a string.
      </>
    ),
    label: "Operation",
    liquid: "context['operation']",
    section: "Sync",
    type: StaticType.STRING,
    variable: VariableType.OPERATION,
  },
];

// The `context` values are provided by `packages/backend/sdk/lib/liquid-template/liquid.ts`.
export function getSyncVariableOptions(
  isRealtimeSync: boolean,
): SyncVariableOption[] {
  return [
    {
      description: <>The ID of the current model as a string.</>,
      label: "Model ID",
      liquid: "context['model_id']",
      section: "Model",
      type: StaticType.STRING,
      variable: VariableType.MODEL_ID,
    },
    {
      description: <>The name of the current model as a string.</>,
      label: "Model name",
      liquid: "context['model_name']",
      section: "Model",
      type: StaticType.STRING,
      variable: VariableType.MODEL_NAME,
    },
    {
      description: <>The ID of the current sync as an integer.</>,
      label: "Sync ID",
      liquid: "context['sync_id']",
      section: "Sync",
      type: StaticType.NUMBER,
      variable: VariableType.SYNC_ID,
    },
    { ...currentTimestampOption, liquid: "context['timestamp']" },
    ...(isRealtimeSync
      ? realtimeEventVariableOptions
      : batchSyncRunVariableOptions),
  ];
}

export function getVariableOptions({
  scope,
}: {
  scope: VariableScope;
}): VariableOption[] {
  return scope === "realtime-journey"
    ? journeyVariableOptions
    : getSyncVariableOptions(scope === "realtime-audience");
}

function groupBySection<T extends VariableOption>(
  options: T[],
): VariableOptionSection<T>[] {
  return VARIABLE_SECTION_ORDER.flatMap((label) => {
    const sectionOptions = options.filter((option) => option.section === label);
    return sectionOptions.length ? [{ label, options: sectionOptions }] : [];
  });
}

export function getVariableSections(
  scope: VariableScope,
): VariableOptionSection[] {
  return groupBySection(getVariableOptions({ scope }));
}

export function getTemplateMetadataSections(
  isRealtimeSync: boolean,
): VariableOptionSection<SyncVariableOption>[] {
  return groupBySection(getSyncVariableOptions(isRealtimeSync));
}
